import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { LineChart } from "@mui/x-charts/LineChart";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import {
  CustomerVisitPerformanceSummary,
  CustomerVisitPerformanceTrendMonth,
  GetCustomerVisitPerformanceResponse,
} from "subModule/src/services/analytics/interface";
import { useGetCustomerVisitPerformanceService } from "subModule/src/services/analytics/useAnalytics";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  dashboardWidgetTitleSx,
  dashboardWidgetIconSx,
  dashboardWidgetTitleRowSx,
} from "components/Analytics/dashboardWidgetLayout";

interface CustomerVisitPerformanceProps {
  themes: any;
  enqueueSnackbar: EnqueueSnackbar;
  globalFilters: {
    stationId: any;
    range: any;
    allStation: boolean;
    businessId?: any;
  };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
  stationsLoaded: boolean;
}

const emptySummary = (): CustomerVisitPerformanceSummary => ({
  newVisits: 0,
  returningVisits: 0,
  totalVisits: 0,
  avgPerMonth: 0,
});

const SERIES = [
  {
    key: "newVisits" as const,
    labelKey: "newVisits",
    color: "#3B82F6",
    soft: "#DBEAFE",
  },
  {
    key: "returningVisits" as const,
    labelKey: "returningVisits",
    color: "#10B981",
    soft: "#D1FAE5",
  },
  {
    key: "totalVisits" as const,
    labelKey: "totalVisits",
    color: "#8B5CF6",
    soft: "#EDE9FE",
  },
];

export default function CustomerVisitPerformance(
  props: CustomerVisitPerformanceProps,
) {
  const { t } = useTranslation();
  const firstRender = useRef(true);
  const isLargeScreen = useMediaQuery(`@media('min-width: 1200px')`);
  const gradientId = useId().replace(/:/g, "");
  const [summary, setSummary] =
    useState<CustomerVisitPerformanceSummary>(emptySummary());
  const [trend, setTrend] = useState<CustomerVisitPerformanceTrendMonth[]>([]);
  const [loading, setLoading] = useState(true);

  const businessId =
    props.globalFilters.businessId?.value ??
    props.globalFilters.businessId ??
    null;

  const { refetch: apiRefetch } = useGetCustomerVisitPerformanceService({
    stationId: props.globalFilters.stationId?.value || null,
    range: props.globalFilters.range?.value,
    allStation: props.globalFilters.allStation,
    ...(businessId ? { businessId: Number(businessId) } : {}),
  });

  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: GetCustomerVisitPerformanceResponse | unknown | any) => {
        if (res?.data?.success) {
          const payload = res?.data?.data || {};
          setSummary(payload.summary || emptySummary());
          setTrend(payload.trend || []);
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied) props.setGlobalFilterApplied(false);
      });
  };

  useEffect(() => {
    if (firstRender.current && props.stationsLoaded) {
      firstRender.current = false;
      setTimeout(() => fetchData(), 50);
    }
  }, [props.stationsLoaded]);

  useEffect(() => {
    if (props.globalFilterApplied) {
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);

  const chartData = useMemo(() => {
    const months = trend.map((item) => item.month);
    return {
      months,
      series: SERIES.map((series) => ({
        id: series.key,
        label: t(series.labelKey),
        data: trend.map((item) => Number(item[series.key] || 0)),
        color: series.color,
        area: true,
        showMark: true,
        curve: "monotoneX" as const,
      })),
    };
  }, [trend, t]);

  const hasData = trend.some(
    (item) =>
      (item.newVisits || 0) +
        (item.returningVisits || 0) +
        (item.totalVisits || 0) >
      0,
  );

  const formatCount = (value: number) =>
    new Intl.NumberFormat("en", {
      maximumFractionDigits: value % 1 === 0 ? 0 : 2,
    }).format(Number(value || 0));

  const primaryColor = props.themes?.primary || "#6366F1";

  return (
    <Card
      sx={{
        boxShadow: "none",
        borderRadius: "8px",
        width: "100%",
        height: "100%",
        maxHeight: { xs: 360, md: 340 },
        display: "flex",
        flexDirection: "column",
        border: "1px solid #E5E7EB",
        overflow: "hidden",
        "& .MuiCardContent-root": {
          padding: "12px !important",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
          boxSizing: "border-box",
        },
      }}
    >
      <CardContent>
        <Box
          sx={{ ...dashboardWidgetHeaderSx, flexShrink: 0, mb: 1.25 }}
        >
          <Box sx={dashboardWidgetTitleRowSx}>
            <Box
              sx={{
                ...dashboardWidgetIconSx,
                width: 32,
                height: 32,
                borderRadius: "8px",
                backgroundColor: props.themes?.primary + "18" || "#2563EB18",
                
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <TrendingUpIcon
                sx={{ fontSize: 17, color: props.themes?.primary || "#2563EB" }}
              />
            </Box>
            <Typography
              className="font-Saira"
              sx={{
                fontSize: { xs: 12, sm: 15 },
              fontWeight: { xs: 600, sm: 700 },
              textAlign: "left",
                color: "#0F2744",
                lineHeight: 1.2,
              }}
            >
              {t("customerVisitPerformance")}
            </Typography>
          </Box>

          <Box className="flex items-center gap-3 flex-wrap">
            {SERIES.map((series) => (
              <Box key={series.key} className="flex items-center gap-1">
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: series.color,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  className="font-Saira"
                  sx={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}
                >
                  {t(series.labelKey)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ mt: 1 }}>
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary, mb: 1 }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary, mb: 1 }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary, mb: 1 }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
          </Box>
        ) : !hasData ? (
          <Box className="flex justify-center flex-col py-8" sx={{ flex: 1 }}>
            <Typography
              className="font-Saira text-center italic font-semibold"
              sx={{ color: props.themes?.primary }}
            >
              {t("noDataAvailable")}
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              className="flex flex-wrap gap-1.5 mb-1.5"
              sx={{ flexShrink: 0 }}
            >
              {SERIES.map((series) => (
                <Box
                  key={series.key}
                  sx={{
                    flex: "1 1 88px",
                    minWidth: 88,
                    borderRadius: "10px",
                    border: "1px solid #EEF0F3",
                    backgroundColor: series.soft,
                    px: 1.25,
                    py: 1,
                  }}
                >
                  <Box className="flex items-center gap-1 mb-0.5">
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        backgroundColor: series.color,
                      }}
                    />
                    <Typography
                      className="font-Saira"
                      sx={{
                        fontSize: 10,
                        color: "#6B7280",
                        fontWeight: 600,
                      }}
                    >
                      {t(series.labelKey)}
                    </Typography>
                  </Box>
                  <Tooltip
                    title={formatCount(summary[series.key])}
                    placement="top"
                    enterDelay={0}
                  >
                    <Typography
                      className="font-Saira"
                      sx={{
                        fontSize: 17,
                        fontWeight: 700,
                        color: series.color,
                        lineHeight: 1.15,
                      }}
                    >
                      {formatCount(summary[series.key])}
                    </Typography>
                  </Tooltip>
                </Box>
              ))}
              <Box
                sx={{
                  flex: "1 1 88px",
                  minWidth: 88,
                  borderRadius: "10px",
                  border: "1px solid #EEF0F3",
                  backgroundColor: "#F3F4F6",
                  px: 1.25,
                  py: 1,
                }}
              >
                <Typography
                  className="font-Saira"
                  sx={{
                    fontSize: 10,
                    color: "#6B7280",
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  {t("avgPerMonth")}
                </Typography>
                <Tooltip
                  title={formatCount(summary.avgPerMonth)}
                  placement="top"
                  enterDelay={0}
                >
                  <Typography
                    className="font-Saira"
                    sx={{
                      fontSize: 17,
                      fontWeight: 700,
                      color: primaryColor,
                      lineHeight: 1.15,
                    }}
                  >
                    {formatCount(summary.avgPerMonth)}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>

            <Box
              sx={{
                width: "100%",
                overflow: "auto",
                flex: 1,
                minHeight: 0,
                display: "flex",
                alignItems: "stretch",
                "&::-webkit-scrollbar": { width: 6, height: 6 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#D1D5DB",
                  borderRadius: 8,
                },
              }}
            >
              <Box
                sx={{
                  minWidth: Math.max(360, chartData.months.length * 56),
                  width: "100%",
                  flex: 1,
                  minHeight: 190,
                  position: "relative",
                }}
              >
                <svg
                  width={0}
                  height={0}
                  aria-hidden
                  style={{ position: "absolute" }}
                >
                  <defs>
                    {SERIES.map((series) => (
                      <linearGradient
                        key={series.key}
                        id={`${gradientId}-${series.key}`}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor={series.color}
                          stopOpacity={0.35}
                        />
                        <stop
                          offset="100%"
                          stopColor={series.color}
                          stopOpacity={0.02}
                        />
                      </linearGradient>
                    ))}
                  </defs>
                </svg>
                <LineChart
                  height={190}
                  xAxis={[
                    {
                      data: chartData.months,
                      scaleType: "point",
                      tickLabelStyle: {
                        fontSize: 10,
                        fill: "#9CA3AF",
                        fontFamily: "Saira, sans-serif",
                      },
                    },
                  ]}
                  series={chartData.series}
                  yAxis={[
                    {
                      min: 0,
                      valueFormatter: (val) =>
                        new Intl.NumberFormat("en", {
                          notation: "compact",
                          compactDisplay: "short",
                          maximumFractionDigits: 1,
                        }).format(Number(val)),
                      tickLabelStyle: {
                        fontSize: 10,
                        fill: "#9CA3AF",
                        fontFamily: "Saira, sans-serif",
                      },
                    },
                  ]}
                  margin={{
                    left: 36,
                    right: isLargeScreen ? 12 : 12,
                    top: 12,
                    bottom: 28,
                  }}
                  grid={{ horizontal: true }}
                  axisHighlight={{ x: "line" }}
                  tooltip={{ trigger: "axis" }}
                  slotProps={{
                    legend: { hidden: true },
                    noDataOverlay: {
                      message: t("noRecordFound"),
                    },
                  }}
                  sx={{
                    [`& .MuiAreaElement-series-newVisits`]: {
                      fill: `url(#${gradientId}-newVisits)`,
                    },
                    [`& .MuiAreaElement-series-returningVisits`]: {
                      fill: `url(#${gradientId}-returningVisits)`,
                    },
                    [`& .MuiAreaElement-series-totalVisits`]: {
                      fill: `url(#${gradientId}-totalVisits)`,
                    },
                    "& .MuiLineElement-root": {
                      strokeWidth: 2.5,
                    },
                    "& .MuiMarkElement-root": {
                      scale: "0.7",
                      fill: "#FFFFFF",
                      strokeWidth: 2,
                    },
                    "& .MuiChartsAxisHighlight-root": {
                      stroke: "#94A3B8",
                      strokeDasharray: "4 4",
                    },
                    "& .MuiChartsGrid-line": {
                      strokeDasharray: "4 4",
                      stroke: "#E5E7EB",
                    },
                    "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": {
                      stroke: "transparent",
                    },
                    "& .MuiChartsTooltip-paper": {
                      borderRadius: "10px",
                      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
                      border: "1px solid #E5E7EB",
                    },
                  }}
                />
              </Box>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}
